// src/components/common/Charts/DashboardCharts.tsx
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import {Bar, Doughnut} from "react-chartjs-2";
import {Card} from "../../ui";
import LoadingSpinner from "../LoadingSpinner";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Chart Data Interface
export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
  }[];
}
// Properties Distribution Chart
interface PropertyDistributionProps {
  data: {
    regions: {name: string; count: number}[];
    types: {name: string; count: number}[];
  };
  isLoading?: boolean;
}

export const PropertyDistributionChart: React.FC<PropertyDistributionProps> = ({
  data,
  isLoading = false,
}) => {
  const regionChartData: ChartData = {
    labels: data.regions.map((r) => r.name),
    datasets: [
      {
        label: "Properties",
        data: data.regions.map((r) => r.count),
        backgroundColor: [
          "#3B82F6",
          "#10B981",
          "#F59E0B",
          "#EF4444",
          "#8B5CF6",
          "#06B6D4",
          "#84CC16",
          "#F97316",
        ],
        borderColor: "#374151",
        borderWidth: 1,
      },
    ],
  };

  const typeChartData: ChartData = {
    labels: data.types.map((t) => t.name),
    datasets: [
      {
        label: "Count",
        data: data.types.map((t) => t.count),
        backgroundColor: [
          "#3B82F6",
          "#10B981",
          "#F59E0B",
          "#EF4444",
          "#8B5CF6",
          "#06B6D4",
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#374151",
        },
        ticks: {
          color: "#9CA3AF",
        },
      },
      x: {
        grid: {
          color: "#374151",
        },
        ticks: {
          color: "#9CA3AF",
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: false,
      },
    },
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Properties by Region */}
      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Properties by Region
        </h3>
        <div className="h-64">
          <Bar data={regionChartData} options={chartOptions} />
        </div>
      </Card>

      {/* Properties by Type */}
      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Properties by Type
        </h3>
        <div className="h-64 flex items-center justify-center">
          <Doughnut data={typeChartData} options={doughnutOptions} />
        </div>
      </Card>
    </div>
  );
};

// Recent Activity Component
interface ActivityItem {
  id: string;
  type:
    | "property_created"
    | "user_registered"
    | "inquiry_received"
    | "contact_submitted";
  message: string;
  timestamp: string;
  user?: {
    name: string;
    avatar?: string;
  };
}

interface RecentActivityProps {
  activities: ActivityItem[];
  isLoading?: boolean;
}

export const RecentActivityCard: React.FC<RecentActivityProps> = ({
  activities,
  isLoading = false,
}) => {
  const getActivityIcon = (type: ActivityItem["type"]) => {
    switch (type) {
      case "property_created":
        return "🏠";
      case "user_registered":
        return "👤";
      case "inquiry_received":
        return "💬";
      case "contact_submitted":
        return "📞";
      default:
        return "📋";
    }
  };

  const getActivityColor = (type: ActivityItem["type"]) => {
    switch (type) {
      case "property_created":
        return "text-green-600";
      case "user_registered":
        return "text-blue-600";
      case "inquiry_received":
        return "text-purple-600";
      case "contact_submitted":
        return "text-orange-600";
      default:
        return "text-gray-600";
    }
  };

  const formatTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor(
      (now.getTime() - activityTime.getTime()) / 1000
    );

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Recent Activity
      </h3>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No recent activity</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full text-sm">
                  {getActivityIcon(activity.type)}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className={getActivityColor(activity.type)}>
                    {activity.message}
                  </span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTimeAgo(activity.timestamp)}
                  {activity.user && (
                    <span className="ml-2">by {activity.user.name}</span>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Quick Stats Grid Component
interface QuickStatsProps {
  stats: {
    totalProperties: number;
    newInquiries: number;
    activeUsers: number;
    conversionRate: number;
  };
  isLoading?: boolean;
}

export const QuickStatsGrid: React.FC<QuickStatsProps> = ({
  stats,
  isLoading = false,
}) => {
  const quickStats = [
    {
      label: "Total Properties",
      value: stats.totalProperties,
      change: "+12%",
      trend: "up" as const,
      color: "text-blue-600",
    },
    {
      label: "New Inquiries",
      value: stats.newInquiries,
      change: "+5%",
      trend: "up" as const,
      color: "text-green-600",
    },
    {
      label: "Active Users",
      value: stats.activeUsers,
      change: "-2%",
      trend: "down" as const,
      color: "text-purple-600",
    },
    {
      label: "Conversion Rate",
      value: `${stats.conversionRate.toFixed(1)}%`,
      change: "+1.2%",
      trend: "up" as const,
      color: "text-orange-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {quickStats.map((stat, index) => (
        <Card key={index} className="p-4">
          {isLoading ? (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
              <div className="h-6 bg-gray-300 rounded w-3/4 mb-1"></div>
              <div className="h-3 bg-gray-300 rounded w-1/3"></div>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {stat.label}
              </p>
              <p className={`text-xl font-semibold ${stat.color} mb-1`}>
                {typeof stat.value === "number"
                  ? stat.value.toLocaleString()
                  : stat.value}
              </p>
              <p
                className={`text-xs ${
                  stat.trend === "up" ? "text-green-600" : "text-red-600"
                }`}
              >
                {stat.change} from last month
              </p>
            </>
          )}
        </Card>
      ))}
    </div>
  );
};
